import { useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { Advance, MoneyToVendor } from '../api/types'
import {
  Amt,
  Card,
  ConfirmDelete,
  DateChip,
  Dt,
  Empty,
  Note,
  NumberInput,
  PageHead,
  Toast,
} from '../components/ui'
import { PersonPicker } from '../components/PersonPicker'
import { useApi } from '../hooks/useApi'
import { useVendorPicker } from '../hooks/usePickers'
import { formatMoney, today } from '../lib/format'

/** The two kinds this screen can write. A کٹوتی is taken on the bill instead. */
type Writable = Exclude<MoneyToVendor, 'RECOVERY'>

const LABEL: Record<MoneyToVendor, string> = {
  ADVANCE: 'ایڈوانس',
  PAYMENT: 'ادائیگی',
  // Never written here - it is a decision taken on the bill, where the shop can
  // see what the flowers earned first. It still has to be named, because the
  // list below shows every row against a grower and a kind with no word for it
  // would print as blank.
  RECOVERY: 'ایڈوانس کٹوتی',
}

/* Written out per kind rather than built from the label: ایڈوانس takes دیا and
   ادائیگی takes دی, and a line that reads wrong in the shop's own language is a
   line nobody trusts. */
const SAVED: Record<Writable, string> = {
  ADVANCE: 'ایڈوانس لکھ دیا',
  PAYMENT: 'ادائیگی لکھ دی',
}

/**
 * Money handed to a grower, of either kind.
 *
 * <p>An ایڈوانس is given before the flowers have earned it. An ادائیگی is the
 * shop settling up - handing over what a delivery actually made. Both come off
 * what the shop owes by the same arithmetic, so they share this screen and this
 * list; they are kept apart because a grower reading his bill is owed the
 * difference between "you took 2,000 ahead" and "you were paid 10,500".
 *
 * <p>Before this, the only way to record paying a grower was to call it an
 * advance - so a settled account still read as owing, and the bill said the
 * money had been taken in advance rather than paid.
 */
export function Advances() {
  const vendors = useVendorPicker()
  const [vendorId, setVendorId] = useState<string | null>(null)
  const advances = useApi<Advance[]>(vendorId ? `/vendors/${vendorId}/advances` : null)

  const [kind, setKind] = useState<Writable>('ADVANCE')
  const [date, setDate] = useState(today())
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  /* Holds the button down while the money is being written, the same as the
     customer side does. Two taps on a slow phone are two entries otherwise, and
     an extra 4,310 in a grower's account is not a mistake anyone enjoys finding. */
  const [busy, setBusy] = useState(false)

  const rows = advances.data ?? []
  const sum = (of: MoneyToVendor) =>
    rows.filter((row) => row.kind === of).reduce((total, row) => total + Number(row.amount), 0)
  const advanceTotal = sum('ADVANCE')
  const paidTotal = sum('PAYMENT')

  const chosen = vendors.people.find((person) => person.id === vendorId)
  const vendor = vendors.rows.find((row) => row.vendorId === vendorId)

  /*
   * The figure under his name, and it is a different figure on each side.
   *
   * <p>On the ایڈوانس side it is what he is still holding: everything taken
   * ahead, less whatever has been clawed back on a bill. Worked out from the
   * rows already on this screen, so it costs nothing and cannot drift from the
   * list printed below it.
   *
   * <p>On the ادائیگی side it is what the shop owes him - the same figure the
   * picker shows beside his name, so the two can never disagree.
   */
  const standing =
    kind === 'ADVANCE'
      ? advanceTotal - sum('RECOVERY')
      : vendor
        ? Number(vendor.net)
        : null

  const paying = Number(amount)
  const after =
    standing !== null && amount !== '' && Number.isFinite(paying)
      ? kind === 'ADVANCE'
        ? standing + paying
        : standing - paying
      : null

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setDone(null)
    setBusy(true)
    try {
      await api.post(`/vendors/${vendorId}/advances`, { date, amount, note: note || null, kind })
      setDone(`${SAVED[kind]} — ${Number(amount).toLocaleString('en-US')}`)
      setAmount('')
      setNote('')
      advances.reload()
      vendors.reload()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: string) {
    setError(null)
    try {
      await api.delete(`/vendors/advances/${id}`)
      advances.reload()
      vendors.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <>
      <PageHead
        title="ایڈوانس اور ادائیگی"
        hint="زمیندار کو دیے ہوئے پیسے۔"
        aside={<DateChip value={date} onChange={setDate} />}
      />

      <Card>
        {error && <Note kind="error">{error}</Note>}
        {done && <Note kind="info">{done}</Note>}
        <Toast message={done} />

        <form onSubmit={submit}>
          {/* The choice sits first, because it changes what the rest of the form
              means - and because picking the wrong one puts the right money
              under the wrong word on the grower's bill. */}
          <div className="kindseg">
            {(['ADVANCE', 'PAYMENT'] as const).map((option) => (
              <button
                key={option}
                type="button"
                className={kind === option ? 'is-on' : ''}
                onClick={() => setKind(option)}
              >
                {LABEL[option]}
              </button>
            ))}
          </div>
          <p className="segnote">
            {kind === 'ADVANCE'
              ? 'مال بکنے سے پہلے دیے ہوئے پیسے'
              : 'حساب برابر کرتے وقت دیے ہوئے پیسے'}
          </p>

          {/*
            The grower, and beneath him the one figure that bears on the choice
            being made - which is not the same figure on both sides.

            <p>Handing out an advance, what matters is how much of the shop's
            money he is already holding: nobody decides that number without
            first asking it, and until now the asking meant leaving the screen.
            Settling up, the advance is beside the point and what matters is
            what he is owed. Same place, different question, because they are
            different questions.
          */}
          <div className="buyer">
            {chosen ? (
              <span className="buyer__who">
                <b>{chosen.name}</b>
              </span>
            ) : (
              <span className="buyer__who muted">زمیندار چنیں</span>
            )}

            <PersonPicker
              label=""
              noun="زمیندار"
              vendor
              triggerLabel={chosen ? 'بدلیں' : undefined}
              value={vendorId}
              onChange={setVendorId}
              people={vendors.people}
              recentIds={vendors.recentIds}
              loading={vendors.loading}
            />
          </div>

          {/*
            A grower's standing can be either way round, and the wording has to
            follow it. Wahid khuzdar has taken 2.81 لاکھ ahead against flowers
            that earned less, so the shop owes him nothing - it is he who is
            over-drawn. Printing «دینا ہے −1,28,873» there would be a minus sign
            the reader has to interpret, in the one place he should not have to.
          */}
          {chosen && standing !== null && (
            <div className="standing">
              <span>
                {kind === 'ADVANCE'
                  ? 'پہلے سے گیا ہوا'
                  : standing > 0
                    ? 'اِس وقت دینا ہے'
                    : 'اُس نے زیادہ لے رکھا ہے'}
              </span>
              <b>{formatMoney(Math.abs(standing))}</b>
            </div>
          )}

          <label className="big-field">
            <span className="field-head">کتنے دے رہے ہیں؟</span>
            <NumberInput value={amount} onChange={setAmount} placeholder="0" />
          </label>

          {/* What it comes to once this is written, before it is written. */}
          {after !== null && (
            <div className="after-line">
              <span>
                {kind === 'ADVANCE'
                  ? 'کل ایڈوانس ہو جائے گا'
                  : after < 0
                    ? 'اُس نے اتنا زیادہ لے رکھا ہو گا'
                    : 'اس کے بعد دینا رہے گا'}
              </span>
              <b>{formatMoney(Math.abs(after))}</b>
            </div>
          )}

          <details className="more-field">
            <summary>تفصیل لکھنی ہے؟</summary>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              placeholder="مثلاً: نقد دیا"
            />
          </details>

          <button className="btn btn--save" disabled={!vendorId || !amount || busy}>
            {busy ? 'لکھ رہے ہیں…' : `${LABEL[kind]} محفوظ کریں`}
          </button>
        </form>
      </Card>

      {vendorId && (
        <Card title="دیے ہوئے پیسے">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>تاریخ</th>
                  <th>کیا تھا</th>
                  <th>رقم</th>
                  <th>تفصیل</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className={row.kind === 'PAYMENT' ? 'row-payment' : undefined}>
                    <td>
                      <Dt value={row.date} />
                    </td>
                    <td>{LABEL[row.kind]}</td>
                    <td className="num">
                      <Amt value={row.amount} />
                    </td>
                    <td>{row.note ?? '—'}</td>
                    <td>
                      {/* Asks first, as the customer side already does. One tap
                          was removing a grower's money outright, and the sums
                          on this screen are the large ones - an advance runs to
                          lakhs, and there is no undo but typing it again from
                          memory. */}
                      <ConfirmDelete onConfirm={() => void remove(row.id)} />
                    </td>
                  </tr>
                ))}

                {rows.length > 0 && (
                  <>
                    <tr className="is-total">
                      <td colSpan={2}>کل ایڈوانس</td>
                      <td className="num">
                        <Amt value={advanceTotal} />
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                    <tr className="is-total">
                      <td colSpan={2}>کل ادائیگی</td>
                      <td className="num">
                        <Amt value={paidTotal} />
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </>
                )}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5}>
                      <Empty>اِس زمیندار کو ابھی کچھ نہیں دیا گیا۔</Empty>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="muted small" style={{ marginTop: 10 }}>
            دونوں «زمیندار کھاتہ» والے بل پر الگ الگ دکھتے ہیں، اور دونوں مال کی رقم میں سے کٹتے
            ہیں۔
          </p>
        </Card>
      )}
    </>
  )
}
